// pages/patients/Patients.jsx

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  Box,
  Grid,
  Typography,
  Button,
  TablePagination,
  Stack,
  CircularProgress,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Chip,
  Paper,
  TextField,
  Autocomplete
} from '@mui/material';
import {
  Add as AddIcon,
  FileDownload as FileDownloadIcon,
  Description as DescriptionIcon,
  TableChart as TableChartIcon,
  LocalHospital as DoctorIcon,
  KeyboardArrowDown as ArrowDownIcon,
  FilterList,
  Clear as ClearIcon,
  Person as PersonIcon,
  Wc as GenderIcon,
  CalendarMonth as AgeIcon
} from '@mui/icons-material';

// --- Child Components ---
import PatientCard from './patientCard';
import SearchBar from '../../components/common/SearchBar';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import NoPrivilege from '../../components/common/NoPrivilege';
import NoContent from '../../components/common/NoContent';
import PatientFormDialog from './PatientForm';
import MultiSelectSearchAutocomplete from '../../components/common/MultiSelectSearchAutocomplete';

// --- Hooks & Services ---
import { useRTL } from '../../hooks/useRTL';
import { patientAPI, doctorAPI } from '../../services/api';
import { searchDoctors } from '../../services/optimizedSearchService';
import { toast } from 'react-toastify';

// Export format options
const EXPORT_FORMATS = {
  simple: {
    key: 'simple',
    labelKey: 'patients.export.simple',
    defaultLabel: 'Simple Export',
    descriptionKey: 'patients.export.simpleDesc',
    defaultDescription: 'Basic patient information',
    icon: <DescriptionIcon fontSize="small" />
  },
  detailed: {
    key: 'detailed',
    labelKey: 'patients.export.detailed',
    defaultLabel: 'Detailed Export',
    descriptionKey: 'patients.export.detailedDesc',
    defaultDescription: 'Includes medical history in separate sheet',
    icon: <TableChartIcon fontSize="small" />
  },
  byDoctor: {
    key: 'byDoctor',
    labelKey: 'patients.export.byDoctor',
    defaultLabel: 'Export by Doctor',
    descriptionKey: 'patients.export.byDoctorDesc',
    defaultDescription: 'Grouped by referring doctor',
    icon: <DoctorIcon fontSize="small" />
  },
  byGender: {
    key: 'byGender',
    labelKey: 'patients.export.byGender',
    defaultLabel: 'Export by Gender',
    descriptionKey: 'patients.export.byGenderDesc',
    defaultDescription: 'Grouped by gender',
    icon: <GenderIcon fontSize="small" />
  },
  byAge: {
    key: 'byAge',
    labelKey: 'patients.export.byAge',
    defaultLabel: 'Export by Age Group',
    descriptionKey: 'patients.export.byAgeDesc',
    defaultDescription: 'Grouped by age ranges',
    icon: <AgeIcon fontSize="small" />
  }
};

const Patients = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, isSuperAdmin } = useAuth();
  const [searchParams] = useSearchParams();
  const { isRTL } = useRTL();

  // --- Privileges ---
  const canAdd = isSuperAdmin || user?.privileges?.some(p => p.module === 'patients' && p.operations.includes('create'));
  const canEdit = isSuperAdmin || user?.privileges?.some(p => p.module === 'patients' && p.operations.includes('update'));
  const canDelete = isSuperAdmin || user?.privileges?.some(p => p.module === 'patients' && p.operations.includes('delete'));
  const canView = isSuperAdmin || user?.privileges?.some(p => p.module === 'patients' && p.operations.includes('view'));
  const canExport = canView;

  // --- Filter Options ---
  const SORT_BY_OPTIONS = [
    { value: 'name', label: t('common.name', 'Name') },
    { value: 'age', label: t('patients.age', 'Age') },
    { value: 'createdAt', label: t('common.createdAt', 'Created At') },
    { value: 'updatedAt', label: t('common.updatedAt', 'Updated At') },
  ];

  const SORT_ORDER_OPTIONS = [
    { value: 'asc', label: t('common.ascending', 'Ascending') },
    { value: 'desc', label: t('common.descending', 'Descending') },
  ];

  const GENDER_OPTIONS = [
    { value: '', label: t('common.all', 'All') },
    { value: 'male', label: t('common.male', 'Male') },
    { value: 'female', label: t('common.female', 'Female') },
    { value: 'other', label: t('common.other', 'Other') },
  ];

  // --- Component State ---
  const [patients, setPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openFormDialog, setOpenFormDialog] = useState(false);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);

  // --- Export States ---
  const [exporting, setExporting] = useState(false);
  const [exportMenuAnchor, setExportMenuAnchor] = useState(null);
  const exportMenuOpen = Boolean(exportMenuAnchor);

  // --- Doctor Data for MultiSelect ---
  const [doctors, setDoctors] = useState([]);

  // --- Filtering & Pagination State ---
  const [filters, setFilters] = useState({
    search: searchParams.get('search') || '',
    gender: searchParams.get('gender') || '',
    doctorReferredIds: searchParams.get('doctorReferredIds')
      ? searchParams.get('doctorReferredIds').split(',').filter(Boolean)
      : [],
    sortBy: searchParams.get('sortBy') || 'createdAt',
    sortOrder: searchParams.get('sortOrder') || 'desc',
  });

  const [pagination, setPagination] = useState({
    page: searchParams.get('page') ? Math.max(1, parseInt(searchParams.get('page'))) : 1,
    limit: parseInt(searchParams.get('limit')) || 9,
    total: 0,
    pages: 0
  });

  // ============== Memoized Options ==============
  const doctorOptions = useMemo(() => doctors.slice(0, 20), [doctors]);

  // ============== Label Functions ==============
  const getDoctorOptionLabel = useCallback((option) => {
    const spec = option?.specialization?.name || option?.specialization || '';
    return spec ? `${option?.name || ''} - ${spec}` : (option?.name || '');
  }, []);

  // Check if any filter is active
  const hasActiveFilters = filters.search ||
    filters.gender ||
    (filters.doctorReferredIds && filters.doctorReferredIds.length > 0) ||
    filters.sortBy !== 'createdAt' ||
    filters.sortOrder !== 'desc';

  // --- Data Fetching Effects ---
  useEffect(() => {
    const fetchInitialDoctors = async () => {
      try {
        const response = await doctorAPI.getAll({
          limit: 50,
        });
        console.log('Fetched doctors:', response.data?.data); // Debug log
        setDoctors(response.data?.data || []);
      } catch (error) {
        console.error("Failed to fetch initial doctors:", error);
      }
    };
    if (canView) fetchInitialDoctors();
  }, [canView]);

  // Fetch doctors from URL params on initial load
  useEffect(() => {
    const doctorIdsFromUrl = searchParams.get('doctorReferredIds');
    if (doctorIdsFromUrl) {
      const ids = doctorIdsFromUrl.split(',').filter(Boolean);
      // Check if we need to fetch any doctors not in our list
      const missingIds = ids.filter(id => !doctors.find(d => d._id === id));

      if (missingIds.length > 0 && doctors.length > 0) {
        // Fetch missing doctors
        Promise.all(
          missingIds.map(id =>
            doctorAPI.getOne(id).then(res => res.data?.data?.[0]).catch(() => null)
          )
        ).then(fetchedDoctors => {
          const validDoctors = fetchedDoctors.filter(Boolean);
          if (validDoctors.length > 0) {
            setDoctors(prev => {
              const newDoctors = [...prev];
              validDoctors.forEach(doc => {
                if (!newDoctors.find(d => d._id === doc._id)) {
                  newDoctors.push(doc);
                }
              });
              return newDoctors;
            });
          }
        });
      }
    }
  }, [searchParams, doctors.length]);

  const fetchPatients = useCallback(async () => {
    if (!canView) return;
    setLoading(true);
    try {
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        sortBy: filters.sortBy,
        sortOrder: filters.sortOrder,
      };
      if (filters.search) params.search = filters.search;
      if (filters.gender) params.gender = filters.gender;

      // Send array of doctor IDs
      if (filters.doctorReferredIds && filters.doctorReferredIds.length > 0) {
        params.doctorReferredIds = filters.doctorReferredIds;
      }

      const response = await patientAPI.getAll(params);
      setPatients(response.data.data);
      setPagination(prev => ({
        ...prev,
        total: response.data.pagination.total || 0,
        pages: response.data.pagination.pages || 0
      }));
    } catch (error) {
      console.error("Failed to fetch patients:", error);
      setPatients([]);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, filters, canView]);

  useEffect(() => {
    fetchPatients();
  }, [fetchPatients]);

  // Update URL params
  useEffect(() => {
    const params = new URLSearchParams();
    if (filters.search) params.set('search', filters.search);
    if (filters.gender) params.set('gender', filters.gender);
    if (filters.doctorReferredIds && filters.doctorReferredIds.length > 0) {
      params.set('doctorReferredIds', filters.doctorReferredIds.join(','));
    }
    if (pagination.page > 1) params.set('page', pagination.page);
    if (pagination.limit !== 9) params.set('limit', pagination.limit);
    if (filters.sortBy !== 'createdAt') params.set('sortBy', filters.sortBy);
    if (filters.sortOrder !== 'desc') params.set('sortOrder', filters.sortOrder);
    navigate(`?${params.toString()}`, { replace: true });
  }, [filters, pagination.page, pagination.limit, navigate]);

  // --- Filter Handlers ---
  const handleFilterChange = useCallback((field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  }, []);

  const handleSearchChange = useCallback((query) => {
    setFilters(prev => ({ ...prev, search: query }));
    setPagination(prev => ({ ...prev, page: 1 }));
  }, []);

  const handleSortChange = useCallback((sortBy, sortOrder) => {
    setFilters(prev => ({ ...prev, sortBy, sortOrder }));
    setPagination(prev => ({ ...prev, page: 1 }));
  }, []);

  // ============== Doctor Multi-Select Handler ==============
  const handleDoctorChange = useCallback((ids) => {
    setFilters(prev => ({ ...prev, doctorReferredIds: ids }));
    setPagination(prev => ({ ...prev, page: 1 }));
  }, []);

  const clearFilters = useCallback(() => {
    setFilters({
      search: '',
      gender: '',
      doctorReferredIds: [],
      sortBy: 'createdAt',
      sortOrder: 'desc',
    });
    setPagination(prev => ({ ...prev, page: 1 }));
  }, []);

  // --- Pagination Handlers ---
  const handlePageChange = (event, newPage) => {
    setPagination(prev => ({ ...prev, page: newPage + 1 }));
  };

  const handleLimitChange = (event) => {
    setPagination(prev => ({
      ...prev,
      limit: parseInt(event.target.value, 10),
      page: 1
    }));
  };

  // --- Export Menu Handlers ---
  const handleExportMenuOpen = (event) => {
    setExportMenuAnchor(event.currentTarget);
  };

  const handleExportMenuClose = () => {
    setExportMenuAnchor(null);
  };

  // --- Export Handler ---
  const handleExport = async (format = 'simple') => {
    handleExportMenuClose();
    setExporting(true);

    try {
      const params = {};
      if (filters.search) params.search = filters.search;
      if (filters.gender) params.gender = filters.gender;

      // Send array of doctor IDs for export
      if (filters.doctorReferredIds && filters.doctorReferredIds.length > 0) {
        params.doctorReferredIds = filters.doctorReferredIds;
      }

      let response;
      let filenamePrefix = 'patients';

      switch (format) {
        case 'detailed':
          response = await patientAPI.exportDetailed(params);
          filenamePrefix = 'patients_detailed';
          break;
        case 'byDoctor':
          response = await patientAPI.exportByDoctor(params);
          filenamePrefix = 'patients_by_doctor';
          break;
        case 'byGender':
          response = await patientAPI.exportByGender(params);
          filenamePrefix = 'patients_by_gender';
          break;
        case 'byAge':
          response = await patientAPI.exportByAge(params);
          filenamePrefix = 'patients_by_age';
          break;
        case 'simple':
        default:
          params.exportFormat = 'simple';
          response = await patientAPI.export(params);
          filenamePrefix = 'patients';
          break;
      }

      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      const date = new Date().toISOString().split('T')[0];
      link.download = `${filenamePrefix}_${date}.xlsx`;

      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success(t('patients.exportSuccess', 'Patients exported successfully'));
    } catch (err) {
      console.error('Export error:', err);
      toast.error(
        err.response?.data?.message ||
        t('patients.exportError', 'Failed to export patients')
      );
    } finally {
      setExporting(false);
    }
  };

  // --- Dialog Handlers ---
  const handleOpenDialog = useCallback((patient = null) => {
    setSelectedPatient(patient);
    setOpenFormDialog(true);
  }, []);

  const handleCloseDialog = useCallback(() => {
    setOpenFormDialog(false);
    setSelectedPatient(null);
  }, []);

  const handleFormSubmit = async (values, patientId, mode) => {
    try {
      if (mode === 'edit' && patientId) {
        const updatedPatientRes = await patientAPI.update(patientId, values);
        const updatedPatient = updatedPatientRes.data.data;

        setPatients(prev =>
          prev.map(p => p._id === patientId ? updatedPatient : p)
        );
        toast.success(t('patients.updateSuccess', 'Patient updated successfully'));
      } else {
        const createdPatientRes = await patientAPI.create(values);
        const newPatient = createdPatientRes.data.data;

        setPatients(prev => [newPatient, ...prev]);
        toast.success(t('patients.createSuccess', 'Patient created successfully'));
      }

      setOpenFormDialog(false);
      setSelectedPatient(null);
    } catch (err) {
      throw err;
    }
  };

  const handleDeleteClick = useCallback((patient) => {
    setSelectedPatient(patient);
    setOpenConfirmDialog(true);
  }, []);

  const handleDelete = useCallback(async () => {
    if (!selectedPatient) return;
    try {
      await patientAPI.delete(selectedPatient._id);
      if (patients.length === 1 && pagination.page > 1) {
        setPagination(prev => ({ ...prev, page: prev.page - 1 }));
      } else {
        fetchPatients();
      }
      toast.success(t('patients.deleteSuccess', 'Patient deleted successfully'));
    } catch (error) {
      console.error(t('patients.deleteError'), error);
      toast.error(t('patients.deleteError', 'Failed to delete patient'));
    } finally {
      setOpenConfirmDialog(false);
      setSelectedPatient(null);
    }
  }, [selectedPatient, patients.length, pagination.page, fetchPatients, t]);

  // ============== Helper Functions ==============
  const getSelectedDoctorNames = useCallback(() => {
    return filters.doctorReferredIds
      .map(id => doctors.find(d => d._id === id))
      .filter(Boolean)
      .map(d => d.name);
  }, [filters.doctorReferredIds, doctors]);

  // --- Permission Check ---
  if (!canView) {
    return (
      <NoPrivilege
        message={t('patients.noPrivilegeError', 'You do not have permission to view patients.')}
      />
    );
  }

  // --- Render ---
  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box display="flex" alignItems="center" gap={2}>
          <Typography variant="h4" component="h1">
            {t('patients.title', 'Patients')}
          </Typography>
          {!loading && (
            <Chip
              label={`${pagination.total} ${t('common.total', 'total')}`}
              color="primary"
              variant="outlined"
              size="small"
            />
          )}
        </Box>
        <Stack direction="row" spacing={1}>
          {/* Export Button with Dropdown */}
          {canExport && (
            <>
              <Button
                variant="outlined"
                startIcon={exporting ? <CircularProgress size={18} /> : <FileDownloadIcon />}
                endIcon={<ArrowDownIcon />}
                onClick={handleExportMenuOpen}
                disabled={exporting || loading || pagination.total === 0}
                aria-controls={exportMenuOpen ? 'patient-export-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={exportMenuOpen ? 'true' : undefined}
              >
                {exporting
                  ? t('common.exporting', 'Exporting...')
                  : t('common.export', 'Export')
                }
              </Button>
              <Menu
                id="patient-export-menu"
                anchorEl={exportMenuAnchor}
                open={exportMenuOpen}
                onClose={handleExportMenuClose}
                MenuListProps={{
                  'aria-labelledby': 'patient-export-button',
                }}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
              >
                {/* Simple Export */}
                <MenuItem onClick={() => handleExport('simple')} disabled={exporting}>
                  <ListItemIcon>
                    {EXPORT_FORMATS.simple.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={t(EXPORT_FORMATS.simple.labelKey, EXPORT_FORMATS.simple.defaultLabel)}
                    secondary={t(EXPORT_FORMATS.simple.descriptionKey, EXPORT_FORMATS.simple.defaultDescription)}
                  />
                </MenuItem>

                <Divider />

                {/* Detailed Export */}
                <MenuItem onClick={() => handleExport('detailed')} disabled={exporting}>
                  <ListItemIcon>
                    {EXPORT_FORMATS.detailed.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={t(EXPORT_FORMATS.detailed.labelKey, EXPORT_FORMATS.detailed.defaultLabel)}
                    secondary={t(EXPORT_FORMATS.detailed.descriptionKey, EXPORT_FORMATS.detailed.defaultDescription)}
                  />
                </MenuItem>

                <Divider />

                {/* Export by Doctor */}
                <MenuItem onClick={() => handleExport('byDoctor')} disabled={exporting}>
                  <ListItemIcon>
                    {EXPORT_FORMATS.byDoctor.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={t(EXPORT_FORMATS.byDoctor.labelKey, EXPORT_FORMATS.byDoctor.defaultLabel)}
                    secondary={t(EXPORT_FORMATS.byDoctor.descriptionKey, EXPORT_FORMATS.byDoctor.defaultDescription)}
                  />
                </MenuItem>

                <Divider />

                {/* Export by Gender */}
                <MenuItem onClick={() => handleExport('byGender')} disabled={exporting}>
                  <ListItemIcon>
                    {EXPORT_FORMATS.byGender.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={t(EXPORT_FORMATS.byGender.labelKey, EXPORT_FORMATS.byGender.defaultLabel)}
                    secondary={t(EXPORT_FORMATS.byGender.descriptionKey, EXPORT_FORMATS.byGender.defaultDescription)}
                  />
                </MenuItem>

                <Divider />

                {/* Export by Age */}
                <MenuItem onClick={() => handleExport('byAge')} disabled={exporting}>
                  <ListItemIcon>
                    {EXPORT_FORMATS.byAge.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={t(EXPORT_FORMATS.byAge.labelKey, EXPORT_FORMATS.byAge.defaultLabel)}
                    secondary={t(EXPORT_FORMATS.byAge.descriptionKey, EXPORT_FORMATS.byAge.defaultDescription)}
                  />
                </MenuItem>
              </Menu>
            </>
          )}
          {canAdd && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
            >
              {t('patients.addTitle', 'Add Patient')}
            </Button>
          )}
        </Stack>
      </Box>

      {/* Filters Paper */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction="row" alignItems="center" gap={2} mb={2}>
          <FilterList />
          <Typography variant="h6">{t('filters', 'Filters')}</Typography>
          {hasActiveFilters && (
            <Chip
              label={t('common.filtersApplied', 'Filters will be applied to export')}
              size="small"
              color="info"
              variant="outlined"
            />
          )}
        </Stack>

        {/* Filter Row 1 - Search, Gender, Doctor */}
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center" mb={2}>
          <SearchBar
            onSearch={handleSearchChange}
            initialValue={filters.search}
            placeholder={t('patients.searchPlaceholder', 'Search by name or phone number...')}
            sx={{ flexGrow: 1, minWidth: '250px' }}
          />

          {/* Gender Filter */}
          <Autocomplete
            options={GENDER_OPTIONS}
            getOptionLabel={(option) => option.label}
            value={GENDER_OPTIONS.find(opt => opt.value === filters.gender) || GENDER_OPTIONS[0]}
            onChange={(event, newValue) => handleFilterChange('gender', newValue?.value || '')}
            renderInput={(params) => (
              <TextField {...params} label={t('patients.gender', 'Gender')} size="small" />
            )}
            sx={{ minWidth: 150 }}
            disableClearable
          />

          {/*   Doctor Filter - Multi-Select with Real-Time Search */}
          <MultiSelectSearchAutocomplete
            label={t('patients.filterByDoctor', 'Filter by Doctor')}
            searchFunction={searchDoctors}
            value={filters.doctorReferredIds || []}
            onChange={handleDoctorChange}
            initialOptions={doctorOptions}
            allItems={doctors}
            getOptionLabel={getDoctorOptionLabel}
            minWidth={250}
          />

          {/* Sort By */}
          <Autocomplete
            options={SORT_BY_OPTIONS}
            getOptionLabel={(option) => option.label}
            value={SORT_BY_OPTIONS.find(opt => opt.value === filters.sortBy)}
            onChange={(event, newValue) => handleSortChange(newValue?.value || 'createdAt', filters.sortOrder)}
            renderInput={(params) => (
              <TextField {...params} label={t('common.sortBy', 'Sort By')} size="small" />
            )}
            sx={{ minWidth: 150 }}
            disableClearable
          />

          <Autocomplete
            options={SORT_ORDER_OPTIONS}
            getOptionLabel={(option) => option.label}
            value={SORT_ORDER_OPTIONS.find(opt => opt.value === filters.sortOrder)}
            onChange={(event, newValue) => handleSortChange(filters.sortBy, newValue?.value || 'desc')}
            renderInput={(params) => (
              <TextField {...params} label={t('common.order', 'Order')} size="small" />
            )}
            sx={{ minWidth: 150 }}
            disableClearable
          />

          {/* Clear Filters */}
          {hasActiveFilters && (
            <Button
              variant="text"
              color="inherit"
              onClick={clearFilters}
              startIcon={<ClearIcon />}
              size="small"
            >
              {t('common.clearFilters', 'Clear')}
            </Button>
          )}
        </Stack>
      </Paper>

      {/* Active Filters Display */}
      {hasActiveFilters && (
        <Box display="flex" gap={1} flexWrap="wrap" mb={2}>
          {filters.search && (
            <Chip
              label={`${t('common.search', 'Search')}: ${filters.search}`}
              size="small"
              onDelete={() => handleSearchChange('')}
            />
          )}
          {filters.gender && (
            <Chip
              label={`${t('patients.gender', 'Gender')}: ${GENDER_OPTIONS.find(o => o.value === filters.gender)?.label}`}
              size="small"
              color="secondary"
              onDelete={() => handleFilterChange('gender', '')}
            />
          )}
          {/* Doctor Chips - Multiple */}
          {filters.doctorReferredIds && filters.doctorReferredIds.length > 0 && (
            getSelectedDoctorNames().map((name, index) => (
              <Chip
                key={`doctor-${filters.doctorReferredIds[index]}`}
                label={`${t('patients.filterByDoctor', 'Doctor')}: ${name}`}
                size="small"
                color="primary"
                onDelete={() => {
                  const newIds = filters.doctorReferredIds.filter((_, i) => i !== index);
                  handleDoctorChange(newIds);
                }}
              />
            ))
          )}
        </Box>
      )}

      {/* Results Info */}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('common.showing', 'Showing')} {pagination.total === 0 ? 0 : Math.min((pagination.page - 1) * pagination.limit + 1, pagination.total)}-{Math.min(pagination.page * pagination.limit, pagination.total)} {t('common.of', 'of')} {pagination.total} {t('common.items', 'items')}
      </Typography>

      {/* Content */}
      {loading ? (
        <Paper sx={{ p: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
          <CircularProgress />
        </Paper>
      ) : patients.length === 0 ? (
        <Paper sx={{ p: 4 }}>
          <NoContent
            icon={<PersonIcon sx={{ fontSize: 64 }} />}
            title={
              hasActiveFilters
                ? t('patients.noFilterResults', 'No patients match your filters')
                : t('patients.noData', 'No Patients Found')
            }
            description={
              hasActiveFilters
                ? t('patients.tryAdjustingFilters', 'Try adjusting your filter criteria')
                : t('patients.noDataDescription', 'Start by adding your first patient.')
            }
            action={
              canAdd && !hasActiveFilters && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => handleOpenDialog()}
                >
                  {t('patients.addTitle', 'Add Patient')}
                </Button>
              )
            }
          />
        </Paper>
      ) : (
        <>
          {/* Patient Cards Grid */}
          <Grid
            container
            spacing={3}
            dir={isRTL ? 'rtl' : 'ltr'}
            sx={{
              flexDirection: isRTL ? 'row-reverse' : 'row',
              justifyContent: isRTL ? 'flex-end' : 'flex-start',
            }}
          >
            {patients.map((patient) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={patient._id}>
                <PatientCard
                  patient={patient}
                  onEdit={handleOpenDialog}
                  onDelete={handleDeleteClick}
                  navigate={navigate}
                  canEdit={canEdit}
                  canDelete={canDelete}
                  isRTL={isRTL}
                  t={t}
                />
              </Grid>
            ))}
          </Grid>

          {/* Pagination */}
          <Paper sx={{ mt: 3 }}>
            <TablePagination
              component="div"
              count={pagination.total}
              page={pagination.page - 1}
              onPageChange={handlePageChange}
              rowsPerPage={pagination.limit}
              onRowsPerPageChange={handleLimitChange}
              rowsPerPageOptions={[6, 9, 12, 18, 24]}
              labelRowsPerPage={t('common.rowsPerPage', 'Items per page')}
            />
          </Paper>
        </>
      )}

      {/* Form Dialog */}
      <PatientFormDialog
        open={openFormDialog}
        onClose={handleCloseDialog}
        patient={selectedPatient}
        onSubmit={handleFormSubmit}
      />

      {/* Confirm Delete Dialog */}
      <ConfirmDialog
        open={openConfirmDialog}
        onConfirm={handleDelete}
        onClose={() => setOpenConfirmDialog(false)}
        title={t('patients.deleteConfirmTitle', 'Delete Patient')}
        content={t('patients.deleteConfirmContent', { name: selectedPatient?.name || '' })}
      />
    </Box>
  );
};

export default Patients;